import { useUserContext } from "../../Hooks/useUserContext";
import { AuthLoadingScreen } from "../../Shared/AuthLoadingScreen";
import { useUserService } from "../../Hooks/useUserService";
import { RouterLink } from "../../Shared/RouterLink";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	TextField,
	Button,
	Paper,
	Checkbox,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import green from "@material-ui/core/colors/green";

export function EditUser(props) {
	const user = useUserContext(props.config);
	const userService = useUserService(props.config);
	const isCreateMode = props.match.params.appUserId === undefined;
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [appUser, setAppUser] = useState({
		appUserId: 0,
		username: "",
		firstName: "",
		lastName: "",
		canViewRecipe: isCreateMode,
		canEditRecipe: false,
		canSendToKitchen: false,
		isUserAdmin: false,
		isVolumeToMassAdmin: false,
		isUnitAdmin: false,
		isUnitAppropriationAdmin: false,
		isMetricConversionAdmin: false,
		isTagAdmin: false
	});

	useEffect(() => {
		const title = isCreateMode ? "Create User" : "Edit User";
		document.title = `${title} - ${props.config.appName}`;

		if (!user || !user.isUserAdmin)
			return;

		if (isCreateMode) {
			setIsLoading(false);
		} else {
			const appUserId = parseInt(props.match.params.appUserId);
			userService.getUserById(appUserId, (response) => {
				if (response && response.status === 200 && response.data) {
					setAppUser(response.data);
				}
				setIsLoading(false);
			}, (error) => {
				console.error(error);
				if (error.response && error.response.status === 404) {
					props.history.push("/notfound");
				} else {
					alert("Failed to load user");
				}
				setIsLoading(false);
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function handleChange(event) {
		const { name, value, checked, type } = event.target;
		setAppUser({
			...appUser,
			[name]: type === "checkbox" ? checked : value
		});
	}

	function handleSave() {
		setIsSaving(true);

		const saveFunction = isCreateMode ? userService.createUser : userService.updateUser;
		const saveArgs = isCreateMode ? [appUser] : [appUser.appUserId, appUser];

		saveFunction(...saveArgs, (response) => {
			if (response && response.status === 200) {
				setToastOpen(true);
				setTimeout(() => {
					props.history.push("/admin/users");
				}, 1000);
			} else {
				alert("Failed to save user");
			}
			setIsSaving(false);
		}, (error) => {
			console.error(error);
			alert("Failed to save user: " + (error.response?.data || error.message));
			setIsSaving(false);
		});
	}

	// Wait for auth check to complete
	if (!user.isInitialized) {
		return <AuthLoadingScreen />;
	}

	if (!user || !user.isUserAdmin)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have user admin permissions to view this page!
					</Typography>
				</Grid>
			</Grid>
		);

	if (isLoading)
		return <Typography style={{ padding: 20 }}>Loading...</Typography>;

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					{isCreateMode ? "Create User" : "Edit User"}
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Paper style={{ padding: 20 }}>
					<Grid container spacing={3}>
						{/* Username */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Username (Email):</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="username"
									value={appUser.username}
									onChange={handleChange}
									required
									disabled={!isCreateMode}
								/>
							</Grid>
						</Grid>
						{/* First Name */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>First Name:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="firstName"
									value={appUser.firstName}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Last Name */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Last Name:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="lastName"
									value={appUser.lastName}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Permissions */}
						<Grid item xs={12} style={{ marginTop: 8 }}>
							<Typography variant="h6" gutterBottom>Permissions</Typography>
						</Grid>
						{/* Can View Recipe */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Can View Recipe:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.canViewRecipe}
									onChange={handleChange}
									name="canViewRecipe"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Can Edit Recipe */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Can Edit Recipe:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.canEditRecipe}
									onChange={handleChange}
									name="canEditRecipe"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Can Send To Kitchen */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Can Send to Kitchen:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.canSendToKitchen}
									onChange={handleChange}
									name="canSendToKitchen"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* User Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>User Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isUserAdmin}
									onChange={handleChange}
									name="isUserAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Volume To Mass Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Volume To Mass Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isVolumeToMassAdmin}
									onChange={handleChange}
									name="isVolumeToMassAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Unit Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Unit Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isUnitAdmin}
									onChange={handleChange}
									name="isUnitAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Unit Appropriation Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Unit Appropriation Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isUnitAppropriationAdmin}
									onChange={handleChange}
									name="isUnitAppropriationAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Metric Conversion Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Metric Conversion Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isMetricConversionAdmin}
									onChange={handleChange}
									name="isMetricConversionAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						{/* Tag Admin */}
						<Grid item xs={12} container alignItems="center" spacing={1}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Tag Admin:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<Checkbox
									checked={appUser.isTagAdmin}
									onChange={handleChange}
									name="isTagAdmin"
									color="primary"
								/>
							</Grid>
						</Grid>
						<Grid item xs={12} style={{ marginTop: 16 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								disabled={isSaving}
								style={{ marginRight: 10 }}
							>
								<SaveIcon style={{ marginRight: 10 }} />
								{isSaving ? "Saving..." : "Save"}
							</Button>
							<Button
								variant="outlined"
								component={RouterLink}
								to="/admin/users"
								disabled={isSaving}
							>
								<CancelIcon style={{ marginRight: 10 }} />
								Cancel
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>User saved successfully!</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
